import React, { useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment, { Moment } from 'moment'
import ControllerCard from './controller-card'
import ImageUploader from './image-uploader'
import VideoUploader from './video-uploader'
import Button from 'Components/button'
import { getKey } from 'lib/get-key'
import getBrandInfo from 'lib/getBrandInfo'
import getCarFansOptions from 'lib/getCarFansOptions'
import CommunitySelector, {
  CommunitySelectorStates,
} from './community-selector'
import getUrl from 'lib/url'
import DatetimeSelector from './datetime-selector'
import TopicSelector from './topic-selector'
import fetchResult from 'lib/fetch-topic'
import { ITime } from './time-picker'
import useCache from 'Components/hooks/use-cache-store'
import { getImageUrl, uploadImgList } from 'lib/image-upload'
import Toast from 'Components/toast-messager'
import fetch from 'lib/decorated-fetch'
import { GlobalStoreContext } from 'Components/store'

const album_icon = require('static/album.png')
const video_icon = require('static/video.png')
const community_icon = require('static/community.png')
const topic_icon = require('static/topic.png')
const timer_icon = require('static/timer.png')
const checked_icon = require('static/checked.png')
const unchecked_icon = require('static/unchecked.png')
interface IControllerProps {
  switches?: string[]
  setQASwitch: (s: string) => void
  setSaving?: (b: boolean) => void
  activeNow: string
  className?: string
  onPublish?: (p: IPublishProps) => void
  buttonDisabled: boolean
  publishing: boolean
  defaultCommunity?: {
    series_id: string
    series_name: string
  }
  defaultTopic?: {
    id: number | string
    name: string
  }
}

export interface IVideoInfo {
  video_id: string
  videoInfo: {
    width?: number
    height?: number
    thumb_uri?: string
    video_duration: number
  }
}

export interface IPublishProps {
  imgs: {
    uri: string
    url?: string
  }[]
  video: IVideoInfo | undefined
  topic: {
    id: number | string
    name: string
  }
  async_to_toutiao: boolean
  community: {
    motor_id: string
    series_id: string
    communityName: string
  }
  time: Moment | undefined
  isTimer: boolean
}

const supportedSortedSwitches = [
  'image',
  'video',
  'community',
  'topic',
  'publish_date',
]

export default function Controller(props: IControllerProps) {
  const {
    switches = supportedSortedSwitches,
    setSaving,
    onPublish,
    activeNow,
    className,
    buttonDisabled,
    setQASwitch,
    publishing,
    defaultCommunity,
    defaultTopic,
  } = props
  const [activeSwitch, setActiveSwitch] = useState('')
  const [fileList, setFileList] = useState<
    { file: File; uri: string; url?: string }[]
  >([])
  const [imgList, setImgList] = useState<(string | ArrayBuffer | null)[]>([])
  const [videoFile, setVideoFile] = useState<File | undefined>(undefined)
  const [videoInfo, setVideoInfo] = useState<IVideoInfo | undefined>(undefined)
  const [videoLoading, setVideoLoading] = useState(false)
  const [videoSaved, setVideoSaved] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const communityTypeRef = useRef<'1' | '2'>()
  const initRef = useRef<boolean>()
  const [communityStates, setCommunityStates] =
    useState<CommunitySelectorStates>({
      type: '1',
      form: {
        brand_id: '',
        query: '',
        series_id: '',
      },
      params: {
        series_id: '',
        motor_id: '',
        motor_name: '',
      },
    })
  const [brandList, setBrandList] = useState<any[]>([])
  const [carFansOptions, setCarFansOptions] = useState([])
  const [topic, setTopic] = useState<{
    id: string | number
    name: string
  }>({
    name: '',
    id: 0,
  })
  const [checked, setChecked] = useState(false)
  const [momentIsSelected, setMomentSelected] = useState(false)
  const [selectedDate, setSelectedDate] = useState<undefined | Moment>(
    undefined
  )
  const [selectedTime, setSelectedTime] = useState<undefined | ITime>(undefined)
  const [topicState, setTopicState] = useState({
    topicList: [],
    keyword: '',
    loaded: false,
  })
  const { getStorage, setStorage, clearStorage } = useCache()

  const { state: globalState } = useContext(GlobalStoreContext)

  const communityValue = useMemo(() => {
    const {
      params: { motor_id, motor_name, series_id },
    } = communityStates
    if (motor_id === '' && motor_name === '' && series_id === '') {
      return
    }
    return `${motor_name}_${series_id}_${motor_id}`
  }, [communityStates])

  useEffect(() => {
    const { type } = communityStates
    if (initRef.current === undefined) {
      // 如果此effect先于拿缓存的effect 不作为
      return
    }
    if (initRef.current) {
      // 拿到缓存中的community 不作为
      initRef.current = false
      return
    }
    if (communityTypeRef.current !== type) {
      // 切换tab 清空
      setCommunityStates({
        type,
        form: {
          brand_id: '',
          query: '',
          series_id: '',
        },
        params: {
          series_id: '',
          motor_id: '',
          motor_name: '',
        },
      })
    }
    communityTypeRef.current = communityStates.type
  }, [communityStates])

  useEffect(() => {
    getBrandInfo().then(setBrandList)
  }, [])

  useEffect(() => {
    const {
      form: { brand_id, query, series_id },
      type,
    } = communityStates
    if (type === '1' && brand_id === '' && query === '' && series_id === '') {
      return
    }
    getCarFansOptions({
      type,
      ...communityStates.form,
    }).then(setCarFansOptions)
  }, [communityStates])

  useEffect(() => {
    // 从本地存储拿图片
    setFileList(getStorage(getKey('IMAGES', activeNow)) || [])
  }, [activeNow])
  useEffect(() => {
    // 从本地存储拿视频
    const savedVideoFile = getStorage(getKey('VIDEO', activeNow))
    const savedVideoInfo = getStorage(getKey('VIDEO_INFO', activeNow))
    setVideoFile(savedVideoFile)
    setVideoInfo(savedVideoInfo)
    setVideoSaved(!!savedVideoFile)
  }, [activeNow])
  useEffect(() => {
    // 从本地存储拿车友圈
    const storedCommunityStates = getStorage(
      getKey('COMMUNITY_STATES', activeNow)
    )
    initRef.current = true
    communityTypeRef.current = storedCommunityStates?.type || '1'
    setCommunityStates(
      storedCommunityStates || {
        type: '1',
        form: {
          brand_id: '',
          query: '',
          series_id: '',
        },
        params: {
          series_id: '',
          motor_id: '',
          motor_name: '',
        },
      }
    )
  }, [activeNow])
  useEffect(() => {
    // 从本地存储拿话题
    const storedTopic = defaultTopic || getStorage(getKey('TOPIC', activeNow))
    setTopic(
      storedTopic || {
        id: 0,
        name: '',
      }
    )
  }, [activeNow, defaultTopic])

  useEffect(() => {
    const readers: Promise<string | ArrayBuffer | null>[] = fileList.map(
      ({ file }) => {
        return new Promise((resolve, reject) => {
          const fileReader = new FileReader()
          fileReader.readAsDataURL(file)
          fileReader.onload = () => {
            resolve(fileReader.result)
          }
          fileReader.onerror = (error) => {
            reject(error)
          }
        })
      }
    )

    Promise.all(readers)
      .then(setImgList)
      .catch((e) => {
        console.error(e)
      })
  }, [fileList])

  useEffect(() => {
    if (activeNow === 'qa') {
      if (videoInfo) {
        setQASwitch('video')
        return
      }
      if (imgList.length > 0) {
        setQASwitch('image')
        return
      }
      setQASwitch('')
    }
  }, [activeNow, videoInfo, imgList, setQASwitch])

  const selectedMoment = useMemo(() => {
    const tenMinLater = moment().add(10, 'minutes')
    if (!selectedDate && !selectedTime) {
      return tenMinLater
    }
    const date = selectedDate || tenMinLater
    const time = selectedTime || {
      hour: tenMinLater.hour(),
      minute: tenMinLater.minute(),
      second: tenMinLater.second(),
    }

    const year = date.year()
    const month = date.month()
    const day = date.date()
    const { hour, minute, second } = time
    const d = new Date(year, month, day, hour, minute, second, 0)
    return moment(d)
  }, [selectedDate, selectedTime])
  useEffect(() => {
    // 从本地存储拿日期
    const storedMoment = getStorage(getKey('PUBLISH_DATE', activeNow))
    if (storedMoment) {
      setSelectedDate(storedMoment)
      setSelectedTime({
        hour: storedMoment.hour(),
        minute: storedMoment.minute(),
        second: storedMoment.second(),
      })
      setMomentSelected(true)
    }
  }, [activeNow])
  useEffect(() => {
    // 切换publisher时， switch全部关闭。
    setActiveSwitch('')
  }, [activeNow])

  useEffect(() => {
    const { keyword } = topicState
    fetchResult(keyword, setTopicState)
  }, [topicState.keyword])

  const handleClick = useCallback(
    (name: string) => {
      // 若相同，说明是要关闭。
      setActiveSwitch(activeSwitch === name ? '' : name)
    },
    [activeSwitch]
  )
  const handleClose = useCallback(() => {
    // 若相同，说明是要关闭。
    setActiveSwitch('')
  }, [])
  const handleCheckChange = useCallback(() => {
    setChecked(!checked)
  }, [checked])
  const handleImgChange = useCallback(
    async (files: FileList | null) => {
      if (!files) {
        return
      }
      const fileArray = Array.from(files).slice(0, 9)
      try {
        const resList = await uploadImgList(fileArray)
        const resUrlMap = await getImageUrl(
          resList.filter((d) => d?.uri).map((d) => d!.uri)
        )
        const newFileList = [
          ...fileList,
          ...fileArray
            .map((file, index) => ({
              file,
              uri: resList?.[index]?.uri!,
              url: resUrlMap[resList?.[index]?.uri!]?.main_url,
            }))
            .filter((d) => d.uri),
        ].slice(0, 9)
        setStorage(getKey('IMAGES', activeNow), newFileList, setSaving)
        setFileList(newFileList)
      } catch (error) {
        console.error(error)
        Toast.show({
          text: error,
        })
      }
    },
    [fileList]
  )
  const handleDeleteItem = useCallback(
    (index: number) => {
      fileList.splice(index, 1)
      setFileList([...fileList])
      try {
        setStorage(getKey('IMAGES', activeNow), fileList, setSaving)
      } catch (error) {
        console.error(error)
      }
    },
    [fileList, activeNow, setSaving]
  )

  const handleDeleteAll = useCallback(
    (name: string) => {
      switch (name) {
        case 'image':
          setFileList([])
          clearStorage(getKey('IMAGES', activeNow))
          return
        case 'video':
          setVideoSaved(false)
          setVideoFile(undefined)
          clearStorage(getKey('VIDEO', activeNow))
          clearStorage(getKey('VIDEO_INFO', activeNow))
          return
        case 'community':
          setCommunityStates({
            type: '1',
            form: {
              brand_id: '',
              query: '',
              series_id: '',
            },
            params: {
              series_id: '',
              motor_id: '',
              motor_name: '',
            },
          })
          clearStorage(getKey('COMMUNITY_STATES', activeNow))
          return
        case 'topic':
          setTopic({
            id: 0,
            name: '',
          })
          setTopicState({
            topicList: [],
            keyword: '',
            loaded: false,
          })
          fetchResult('', setTopicState)
          clearStorage(getKey('TOPIC', activeNow))
          return
        case 'publish_date':
          setSelectedDate(undefined)
          setSelectedTime(undefined)
          setMomentSelected(false)
          clearStorage(getKey('PUBLISH_DATE', activeNow))
          return
      }
    },
    [fileList, activeNow, setSaving]
  )

  const handleVideoChange = useCallback((file: File | undefined) => {
    if (!file) {
      return
    }
    setErrorMsg('')
    setVideoFile(file)
    setVideoSaved(false)
  }, [])

  const handleConfirmUpload = useCallback(() => {
    if (!videoFile) {
      return
    }
    if (!['video/quicktime', 'video/mp4'].includes(videoFile.type)) {
      setErrorMsg('请选择有效的视频文件(MP4, Quicktime)')
      return
    }
    setVideoLoading(true)
    fetch(getUrl('/motor/content_publish/upload_auth_token?from_pc=1'))
      .then(async (res) => {
        const { data } = res || {}
        if (!data) {
          setErrorMsg('上传失败, 请重试！')
          return
        }

        const stsToken = {
          CurrentTime: data.start_time,
          ExpiredTime: data.end_time,
          SessionToken: data.auth_token,
          AccessKeyID: data.access_key,
          SecretAccessKey: data.sec_key,
          AccessKeyId: data.access_key,
        }
        const BytedUploader = (await import('@byted/uploader')).default
        const bytedUploader = new BytedUploader({
          userId: globalState?.userData?.user_id || 'pc_ugc_video',
          appId: 1839,
          videoConfig: {
            spaceName: 'motor-ugc',
            processAction: [
              { name: 'GetMeta' },
              {
                name: 'Snapshot',
                input: {
                  // 截图时间
                  SnapshotTime: 0,
                  // 是否跳过黑帧检测，黑帧检测即SnapshotTime处要是是黑帧会往后找不是黑帧的封面。默认false的情况下会找到非黑首帧作为封面
                  SkipBlackDetect: false,
                },
              },
            ],
          },
        })
        const fileKey = bytedUploader.addFile({
          file: videoFile,
          stsToken,
          type: 'video',
        })
        bytedUploader.start(fileKey)

        bytedUploader.on('complete', (infor: any) => {
          const { uploadResult = {} } = infor || {}
          const { VideoMeta = {}, PosterUri } = uploadResult
          const duration = parseInt(VideoMeta.Duration, 10)
          if (duration < 3) {
            setErrorMsg('上传失败，视频时长需大于3秒，请重新上传')
          } else if (duration >= 3 && duration <= 60) {
            const video_info = {
              video_id: uploadResult.Vid,
              videoInfo: {
                width: VideoMeta.Wu,
                height: VideoMeta.Height,
                thumb_uri: PosterUri,
                video_duration: VideoMeta.Duration,
              },
            }
            setStorage(getKey('VIDEO_INFO', activeNow), video_info, undefined)
            setStorage(getKey('VIDEO', activeNow), videoFile, undefined)
            setVideoInfo(video_info)
            setVideoSaved(true)
            setActiveSwitch('')
          } else {
            setErrorMsg('上传失败，视频时长需小于1分钟，请重新上传')
          }

          setVideoLoading(false)
        })
        bytedUploader.on('error', (info: any) => {
          setErrorMsg('上传失败 无法解析文件')
          setVideoLoading(false)
          console.error(info)
        })
      })
      .catch((error) => {
        setErrorMsg('上传失败，请检查网络是否正常')
        setVideoLoading(false)
        console.error(error)
      })
  }, [videoFile, activeNow, setSaving])

  // 拿到车品牌信息
  const handleBrandChange = (value: string | null) => {
    if (!value) {
      return
    }
    setCommunityStates((pre) => ({
      type: pre.type,
      params: {
        motor_id: '',
        motor_name: '',
        series_id: '',
      },
      form: {
        ...pre.form,
        brand_id: value.split('_')[1] || '',
      },
    }))
  }

  // 更换圈子，改变选项卡
  const handleCommunityTypeChange = useCallback((type: '1' | '2') => {
    setCommunityStates((pre) => ({
      ...pre,
      type,
    }))
  }, [])
  // 取消选择
  const handleCommunityCancel = useCallback(() => {
    setCommunityStates({
      type: '1',
      form: {
        brand_id: '',
        query: '',
        series_id: '',
      },
      params: {
        series_id: '',
        motor_id: '',
        motor_name: '',
      },
    })
  }, [])

  const handleCommunityConfirm = useCallback(() => {
    setStorage(
      getKey('COMMUNITY_STATES', activeNow),
      communityStates,
      setSaving
    )
    setActiveSwitch('')
  }, [activeNow, setSaving, communityStates])

  const handleCommunityChange = useCallback(
    (value) => {
      if (!value) {
        return
      }
      const [motor_name = '', series_id = '', motor_id = ''] = value.split('_')
      setCommunityStates({
        ...communityStates,
        params: {
          motor_name,
          series_id,
          motor_id,
        },
      })
    },
    [communityStates]
  )

  const handleDateChange = useCallback((time) => {
    setMomentSelected(true)
    setSelectedDate(time)
  }, [])
  const handleTimeChange = useCallback((time) => {
    setMomentSelected(true)
    setSelectedTime(time)
  }, [])
  const handleTimeCancel = useCallback(() => {
    setMomentSelected(false)
    setSelectedDate(undefined)
    setSelectedTime(undefined)
  }, [])
  const handleTimeConfirm = useCallback(() => {
    setStorage(getKey('PUBLISH_DATE', activeNow), selectedMoment, setSaving)
    setActiveSwitch('')
  }, [activeNow, selectedMoment, setSaving])

  const handleChooseTopic = useCallback(
    (item) => {
      setStorage(getKey('TOPIC', activeNow), item, setSaving)
      setTopic(
        item || {
          id: 0,
          name: '',
        }
      )
      setActiveSwitch('')
    },
    [activeNow, setSaving]
  )

  const handleTopicInputChange = useCallback(
    (e) => {
      setTopicState({
        ...topicState,
        keyword: e.target.value,
        loaded: false,
      })
    },
    [topicState]
  )

  const handlePublish = () => {
    const community = communityValue?.split('_')
    const [communityName, series_id, motor_id] = community || []

    if (onPublish) {
      onPublish({
        imgs: fileList,
        video: videoInfo,
        topic,
        async_to_toutiao: checked,
        community: { motor_id, series_id, communityName },
        time: selectedMoment,
        isTimer: momentIsSelected,
      })
    }
  }

  const shownComponents = useMemo(() => {
    const shownSwitches = supportedSortedSwitches.filter((value) =>
      switches.includes(value)
    )

    return shownSwitches.map((name, index) => {
      let icon
      let text
      let title
      let component = null
      let showDelete = false
      let disabled = false
      switch (name) {
        case 'image':
          icon = album_icon
          text = fileList.length > 0 ? `已选择 ${fileList.length} 张` : '图片'
          title = '上传图片'
          component = (
            <ImageUploader
              imgList={imgList}
              onChange={handleImgChange}
              onDeleteItem={handleDeleteItem}
            />
          )
          showDelete = fileList.length > 0
          break
        case 'video':
          icon = video_icon
          text = !videoSaved ? '视频' : videoFile?.name || ''
          title = '上传视频'
          showDelete = videoSaved
          component = (
            <VideoUploader
              videoFile={videoFile}
              onChange={handleVideoChange}
              onConfirmUpload={handleConfirmUpload}
              saved={videoSaved}
              errMsg={errorMsg}
              loading={videoLoading}
            />
          )
          break
        case 'community':
          icon = community_icon
          if (defaultCommunity) {
            text = defaultCommunity.series_name
          } else {
            text = !communityValue ? '选择车友圈' : communityValue.split('_')[0]
          }
          title = '选择车友圈'
          showDelete = !!communityValue
          disabled = !!defaultCommunity
          component = (
            <CommunitySelector
              brandList={brandList}
              onBrandChange={handleBrandChange}
              onCommunityTypeChange={handleCommunityTypeChange}
              onCancel={handleCommunityCancel}
              onConfirm={handleCommunityConfirm}
              carFansOptions={carFansOptions}
              states={communityStates}
              onCommunityChange={handleCommunityChange}
              communityValue={communityValue}
            />
          )
          break
        case 'topic':
          icon = topic_icon
          console.log('topic', topic)
          text = topic.id === 0 ? '话题' : topic.name
          title = '选择话题'
          showDelete = topic.id !== 0
          component = (
            <TopicSelector
              chooseTopic={handleChooseTopic}
              inputValue={topicState.keyword}
              topicState={topicState}
              onChange={handleTopicInputChange}
            />
          )
          break
        case 'publish_date':
          icon = timer_icon
          text = momentIsSelected
            ? selectedMoment.format('YYYY-MM-DD  HH:MM')
            : '定时发布'
          title = '选择要发布的时间'
          showDelete = momentIsSelected
          component = (
            <DatetimeSelector
              onCancel={handleTimeCancel}
              onConfirm={handleTimeConfirm}
              value={selectedMoment}
              onDateChange={handleDateChange}
              onTimeChange={handleTimeChange}
              momentIsSelected={momentIsSelected}
              //timeConstrains={timeConstrains}
            />
          )
          break
        default:
          throw new Error('Unknown switch.')
      }
      return (
        <ControllerCard
          text={text}
          name={name}
          title={title}
          icon={icon}
          key={`${name} ${index}`}
          showChildren={name === activeSwitch}
          onClick={handleClick}
          showDelete={showDelete}
          onClose={handleClose}
          onDeleteAll={() => {
            handleDeleteAll(name)
          }}
          disabled={disabled}
        >
          {component}
        </ControllerCard>
      )
    })
  }, [
    supportedSortedSwitches,
    switches,
    activeSwitch,
    fileList,
    imgList,
    videoFile,
    videoInfo,
    videoLoading,
    communityStates,
    brandList,
    carFansOptions,
    selectedMoment,
    momentIsSelected,
    communityValue,
    topic,
    topicState,
    errorMsg,
  ])

  return (
    <div className={`${className} controller`}>
      <div className="controller-left-part">{shownComponents}</div>
      <div className="controller-right-part">
        <input
          type="checkbox"
          name="micro-moment"
          className="checked-input"
          checked={checked}
          onChange={handleCheckChange}
        />
        <img
          src={checked ? checked_icon : unchecked_icon}
          className="checked-icon"
        />
        <label htmlFor="micro-moment" className="checked-input-label">
          同步到微头条
        </label>
        <Button
          type="new_primary"
          className="publish-button"
          onClick={handlePublish}
          disabled={buttonDisabled}
          loading={publishing}
        >
          确认发布
        </Button>
      </div>

      <style jsx>
        {`
          .controller {
            position: relative;
          }
          .controller-left-part {
            display: flex;
            align-items: center;
            max-width: 520px;
            flex-wrap: wrap;
          }

          .controller-right-part {
            position: absolute;
            top: 8px;
            right: 0;
            display: inline-flex;
            align-items: center;
          }
          .checked-input {
            position: absolute;
            width: 16px;
            height: 16px;
            border: 1px solid #979aa8;
            opacity: 0;
            cursor: pointer;
          }
          .checked-icon {
            width: 16px;
            height: 16px;
            object-fit: contain;
            object-position: center;
          }
          .checked-input-label {
            margin-left: 4px;
            color: #979aa8;
            font-size: 14px;
            line-height: 18px;
          }
          :global(.controller .publish-button) {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: 8px;
            font-size: 14px;
            font-weight: 500;
            line-height: 20px;
            color: #1f2129;
            margin-left: 8px;
            width: 96px;
            height: 32px;
          }
        `}
      </style>
    </div>
  )
}
